/**
 * TODO: LoadState is reused for two distinct lifecycles:
 * 1. Fetch state (collection-level) — has a collection been fetched from the API?
 * 2. Load state (item-level) — has an individual item been loaded into a provider?
 * Consider splitting into separate FetchState and LoadState enums.
 */
export enum LoadState {
  NotLoaded,
  Loading,
  Reloading,
  LoadComplete,
  Error,
}

export type ItemsNotFetched = {
  state: LoadState.NotLoaded | LoadState.Loading | LoadState.Error;
};

export type ItemsFetched<TItem> = {
  state: LoadState.LoadComplete;
  items: Record<string, TItem>;
};

export type FetchableItems<TItem> = ItemsNotFetched | ItemsFetched<TItem>;

export type LoadableItems<TItem extends LoadableItem> =
  | ItemsNotFetched
  | ItemsFetched<TItem>;

export type LoadableItem = {
  id: string;
  loadState: LoadState;
};

/**
 * Base type for project data state. Each app defines its own concrete
 * ProjectDataState with specific keys and item types.
 * Shared utilities use `TState extends LoadableData` as a generic constraint.
 *
 * All slices must contain items extending `LoadableItem`. Non-loadable
 * lookup data (e.g. classification schemes) should live in separate stores.
 */
export type LoadableData = Record<string, LoadableItems<LoadableItem>>;

/**
 * Contract for instances registered with a data-loading orchestrator
 * (`DataLoadingService`). Used as an identity in provider lists; handlers
 * narrow with a type guard to concrete provider implementations. Implementations
 * should release resources in {@link IDataLoadingProvider.cleanup}.
 */
export interface IDataLoadingProvider {
  cleanup(): Promise<void>;
}

/**
 * Minimum surface for app-specific data-loading orchestrators that feed
 * loadable items into registered providers.
 */
export interface IDataLoadingService {
  registerProvider(provider: IDataLoadingProvider): void;
  unregisterProvider(provider: IDataLoadingProvider): Promise<void>;
}
